/**
 * viewportOrientationPolicy — the "phones are always landscape" policy
 * (U3, 2026-09-28 conductor session 2).
 *
 * DJ's direction: "390x844 obviously fucks up our game pretty bad … it would
 * have to just always be rotated horizontal even if phone position vertical."
 * Portrait phone layouts are therefore NOT a supported game surface: the app
 * presents a rotate-device treatment whenever the viewport looks like a phone
 * held upright, and assumes landscape everywhere else.
 *
 * The decision is deliberately VIEWPORT-BASED ONLY — no user-agent, touch or
 * pointer heuristics. The V1 Playwright harness resizes an ordinary desktop
 * context to 390×844 and must exercise exactly the same policy as a real
 * handset, so the policy cannot depend on device sniffing.
 *
 * Definition:
 *
 *     shouldShowRotateNotice(width, height)
 *       === isPortraitViewport(width, height) && isNarrowViewport(width)
 *       === (height > width) && (width < 600)
 *
 * Pinned examples:
 *   - 390×844  → true   (reference phone portrait)
 *   - 844×390  → false  (phone landscape)
 *   - 1024×768 → false  (desktop / tablet landscape)
 *
 * Threshold rationale for NARROW_VIEWPORT_MAX_WIDTH = 600:
 *   - `height > width` is the universal portrait test; square viewports are
 *     not portrait and do not need rotation.
 *   - Phones in portrait are 320–540 CSS px wide (iPhone 12/13/14-class 390,
 *     Pro Max 430, Pixel 360–412, legacy 320). 600 covers all of them with
 *     headroom while excluding the next class up.
 *   - Every supported desktop viewport this repo tests is >= 600 px wide
 *     (1024×768, 1280×720, 1440×1000; repo breakpoints MOBILE 480 / TABLET
 *     768 / DESKTOP 1024), so desktop windows stay untouched. A deliberately
 *     narrowed desktop window below 600 px wide with a taller-than-wide aspect
 *     also gets the rotate treatment — an accepted consequence of viewport-only
 *     detection (and what makes the harness work).
 *   - 768×1024 tablet portrait stays OUTSIDE the phone class on purpose: the
 *     policy targets phones; tablets keep the existing layout. If the product
 *     decides tablets need the treatment too, raise this single constant.
 */

/** Portrait-and-phone viewports must be narrower than this (CSS px). */
export const NARROW_VIEWPORT_MAX_WIDTH = 600;

/** True when the viewport is taller than it is wide (square is NOT portrait). */
export function isPortraitViewport(width: number, height: number): boolean {
  return Number.isFinite(width) && Number.isFinite(height) && height > width;
}

/** True when the viewport is a plausible phone width (positive, < 600). */
export function isNarrowViewport(width: number): boolean {
  return Number.isFinite(width) && width > 0 && width < NARROW_VIEWPORT_MAX_WIDTH;
}

/**
 * The policy decision used by RotateDeviceNotice + asserted by the U01/V1
 * harness: show the rotate-device treatment only for narrow portrait viewports.
 */
export function shouldShowRotateNotice(width: number, height: number): boolean {
  return isPortraitViewport(width, height) && isNarrowViewport(width);
}
